import React, { useState, useEffect } from 'react'

interface LiveImportModalProps {
  isOpen: boolean
  onClose: () => void
  token: string
  onImportSuccess: () => void
}

interface InfrastructureObject {
  id: number
  name: string
  infrastructure_type: string
}

export default function LiveImportModal({ isOpen, onClose, token, onImportSuccess }: LiveImportModalProps) {
  const [csvFile, setCsvFile] = useState<File | null>(null)
  const [objects, setObjects] = useState<InfrastructureObject[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingObjects, setLoadingObjects] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadObjects()
    }
  }, [isOpen, token])

  const loadObjects = async () => {
    setLoadingObjects(true)
    try {
      console.log('Loading objects from /api/infrastructure/...')
      // Load ALL infrastructure objects, not just those with readings
      const response = await fetch('/api/infrastructure/', {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.ok) {
        const data = await response.json()
        console.log('Loaded objects:', data)
        // Filter to only SUW and Hydroforownia types
        const filtered = data.filter((obj: InfrastructureObject) =>
          obj.infrastructure_type === 'Stacja Uzdatniania wody' ||
          obj.infrastructure_type === 'Hydroforownia'
        )
        console.log('Filtered SUW/Hydroforownia objects:', filtered)
        setObjects(filtered)
      } else {
        console.error('Failed to load objects, status:', response.status)
      }
    } catch (error) {
      console.error('Error loading objects:', error)
    } finally {
      setLoadingObjects(false)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setCsvFile(file)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!csvFile) {
      alert('Proszę wybrać plik CSV')
      return
    }

    setLoading(true)

    try {
      const formData = new FormData()
      formData.append('csv_file', csvFile)

      const response = await fetch('/api/statistics/live-readings/csv-import-general', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      })

      if (response.ok) {
        const result = await response.json()
        alert(`Pomyślnie zaimportowano ${result.imported_count} odczytów live!\n\nSzczegóły:\n- Zaimportowano: ${result.imported_count}\n- Duplikaty pominięte: ${result.duplicate_count || 0}\n- Błędne wiersze: ${result.error_count || 0}\n- Przetworzonych obiektów: ${result.objects_processed || 0}`)
        onImportSuccess()
        onClose()
      } else {
        const error = await response.json()
        console.error('CSV Import error details:', error)
        const errorDetail = error.detail || 'Nieznany błąd'
        alert(`Błąd importu CSV:\n\n${errorDetail}`)
      }
    } catch (error) {
      console.error('Import error:', error)
      alert('Wystąpił błąd podczas importu danych')
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Import danych Live z pliku CSV ⚡
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Infrastructure Objects Reference */}
            <div className="bg-blue-50 p-4 rounded-md">
              <h4 className="text-sm font-medium text-gray-900 mb-2">
                Dostępne obiekty infrastruktury (SUW i Hydroforownie):
              </h4>
              
              {loadingObjects ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mr-2"></div>
                  <span className="text-sm text-gray-600">Ładowanie obiektów...</span>
                </div>
              ) : objects.length > 0 ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs max-h-60 overflow-y-auto">
                    {objects.map(obj => (
                      <div key={obj.id} className="bg-white p-2 rounded border">
                        <div className="font-medium">{obj.name}</div>
                        <div className="text-gray-600">
                          ID: <strong>{obj.id}</strong> • Typ: {obj.infrastructure_type}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    ↑ Te ID możesz używać w plikach CSV - każdy wiersz może mieć inny obiekt
                  </p>
                </>
              ) : (
                <div className="text-center py-4">
                  <p className="text-sm text-gray-600">
                    ⚠️ Brak obiektów typu SUW lub Hydroforownia w systemie
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Najpierw utwórz obiekty infrastruktury w module Infrastruktura
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plik CSV z danymi Live *
              </label>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                required
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Plik powinien zawierać kolumny: <strong>infrastructure_id,reading_type,timestamp,value</strong><br/>
                <strong>Wartości (value) podawaj w LITRACH</strong> - system automatycznie przelicza je na m³ podczas wizualizacji<br/>
                Każdy wiersz może mieć inny obiekt i typ odczytu - system automatycznie obsłuży wszystkie dane
              </p>
            </div>

            {/* Example CSV format */}
            <div className="bg-purple-50 p-3 rounded-md">
              <h4 className="text-sm font-medium text-gray-900 mb-1">Przykładowy format CSV (wartości w LITRACH):</h4>
              <pre className="text-xs text-gray-700 whitespace-pre-wrap">
{`infrastructure_id,reading_type,timestamp,value
1,water_to_network,2025-10-12 09:58:00,125678.45
1,waste_water,2025-10-12 09:58:30,245.23
2,buffer_input,2025-10-12 09:59:00,45678.12
2,network_output,2025-10-12 09:59:15,67234.56`}
              </pre>
              <p className="text-xs text-gray-600 mt-1">
                ⓘ Wartości są w litrach (125678.45L = 125.68 m³). System automatycznie konwertuje na m³ w wykresach<br/>
                ⓘ W jednym pliku możesz mieszać dane z różnych obiektów i typów odczytów
              </p>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50"
              >
                Anuluj
              </button>
              <button
                type="submit"
                disabled={loading}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md font-medium disabled:opacity-50 flex items-center"
              >
                {loading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                )}
                Importuj dane Live
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
